import React from 'react';
import ReactDOM from 'react-dom';
import { createHtmlPortalNode, InPortal, OutPortal } from 'react-reverse-portal';
import { noop } from 'lodash';

import { bemBlock } from '@neptune/shared/venus-ui';

import { useFullScreenAnimation } from './use-fullscreen-animation';

import './FullscreenElementPortal.less';

const block = bemBlock('fullscreen-element-portal');

type TransitionHandler = (newFullscreen: boolean) => void;

export type FullscreenPortalEventsContext = {
  addTransitionHandler: (when: 'before' | 'after', handler: TransitionHandler) => void;
  removeTransitionHandler: (when: 'before' | 'after', handler: TransitionHandler) => void;
};

export const FullscreenPortalEventsContext = React.createContext<FullscreenPortalEventsContext>({
  addTransitionHandler: noop,
  removeTransitionHandler: noop,
});

class FullscreenPortalEvents implements FullscreenPortalEventsContext {
  private handlers = {
    before: new Set<TransitionHandler>(),
    after: new Set<TransitionHandler>(),
  };

  addTransitionHandler = (when: 'before' | 'after', handler: TransitionHandler) => {
    this.handlers[when].add(handler);
  };

  removeTransitionHandler = (when: 'before' | 'after', handler: TransitionHandler) => {
    this.handlers[when].delete(handler);
  };

  callHandlers = (when: 'before' | 'after', newFullscreen: boolean) => {
    for (const handler of this.handlers[when]) {
      handler(newFullscreen);
    }
  };
}

function useFullscreenPortalEvents() {
  return (React.useRef<FullscreenPortalEvents>().current ??= new FullscreenPortalEvents());
}

export const FullscreenElementPortal: React.FC<{
  className?: string;
  fullscreen: boolean;
}> = ({ className, fullscreen, children }) => {
  const [renderInBody, setRenderInBody] = React.useState(false);

  const portalEvents = useFullscreenPortalEvents();

  const onMaximize = React.useCallback(() => {
    portalEvents.callHandlers('before', true);
    setRenderInBody(true);
  }, [portalEvents]);
  const onRestore = React.useCallback(() => {
    portalEvents.callHandlers('before', false);
    setRenderInBody(false);
  }, [portalEvents]);

  const setRef = useFullScreenAnimation(fullscreen, onMaximize, onRestore);

  const portalNodeRef = React.useRef(createHtmlPortalNode());
  const element = portalNodeRef.current.element;

  React.useEffect(() => {
    element.setAttribute('class', block({ extra: className }));
  }, [element, className]);

  React.useEffect(() => {
    setRef(element);
  }, [element, setRef]);

  const inPortal = (
    <InPortal node={portalNodeRef.current}>
      <FullscreenPortalEventsContext.Provider value={portalEvents}>
        {children}
      </FullscreenPortalEventsContext.Provider>
    </InPortal>
  );
  const outPortal = <OutPortal node={portalNodeRef.current} />;

  React.useLayoutEffect(() => {
    // This will be called right after the portal is rendered in the new position.
    portalEvents.callHandlers('after', renderInBody);
  }, [element, portalEvents, renderInBody]);

  return (
    <>
      {/* It is crucial that OutPortal is rendered before InPortal, so that children
          are rendered into DOM and not a detached node. */}
      {renderInBody ? ReactDOM.createPortal(outPortal, document.body) : outPortal}
      {inPortal}
    </>
  );
};
